import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Questionnaire, QuestionnaireResponse } from '../../types/fhir';
import { getAssetFileName, resolveAppAssetUrl } from '../../helpers/appAssets';
import {
    getAvailableAudiences,
    getDefaultAudience,
    getMaterialDocument,
    resolveTherapyMaterialBlocks,
    ResolvedTherapyMaterialBlock,
    TherapyMaterialAudience,
    TherapyMaterialContentItem,
} from './therapyMaterial';
import './TherapyMaterialRenderer.css';

type Props = {
    questionnaire: Questionnaire;
    questionnaireResponse?: QuestionnaireResponse | null;
    kind?: 'therapy-material' | 'nachsorge';
    // Reports the content version shown, e.g. so a PDF export can use the same one.
    onAudienceChange?: (audience: TherapyMaterialAudience) => void;
};

const TherapyMaterialImage = ({
    item,
    variant,
}: {
    item: TherapyMaterialContentItem;
    variant?: 'small';
}): JSX.Element => {
    const { t } = useTranslation();
    const src = resolveAppAssetUrl(item.path);
    const [hasFailed, setHasFailed] = useState(false);

    useEffect(() => {
        setHasFailed(false);
    }, [src]);

    if (!src || hasFailed) {
        return (
            <div
                className="therapy-material__image-placeholder"
                role="img"
                aria-label={t('Image will appear here in the patient app.')}
            >
                <span className="therapy-material__image-icon" aria-hidden="true">
                    ▧
                </span>
                <strong>{t('Image will appear here in the patient app.')}</strong>
                {item.path && <span>{getAssetFileName(item.path)}</span>}
            </div>
        );
    }

    return (
        <figure className={`therapy-material__image ${variant ? `therapy-material__image--${variant}` : ''}`}>
            {item.header && <figcaption>{item.header}</figcaption>}
            <img
                src={src}
                alt={item.header || getAssetFileName(item.path)}
                width={item.w || undefined}
                height={item.h || undefined}
                loading="lazy"
                onError={() => setHasFailed(true)}
            />
        </figure>
    );
};

const TherapyMaterialRenderer = ({
    questionnaire,
    questionnaireResponse,
    kind = 'therapy-material',
    onAudienceChange,
}: Props): JSX.Element => {
    const { t } = useTranslation();
    const isNachsorge = kind === 'nachsorge';
    const parsed = useMemo(() => {
        try {
            return {
                document: getMaterialDocument(questionnaire, kind),
                error: false,
            };
        } catch {
            return { document: null, error: true };
        }
    }, [questionnaire, kind]);
    const [audience, setAudience] = useState<TherapyMaterialAudience>(
        parsed.document ? getDefaultAudience(parsed.document) : 'female',
    );

    useEffect(() => {
        if (parsed.document) {
            setAudience(getDefaultAudience(parsed.document));
        }
    }, [parsed.document]);

    useEffect(() => {
        onAudienceChange?.(audience);
    }, [audience, onAudienceChange]);

    if (parsed.error || !parsed.document) {
        return (
            <div className="therapy-material__error" role="alert">
                <strong>
                    {isNachsorge
                        ? t('This aftercare material cannot be displayed.')
                        : t('This therapy material cannot be previewed.')}
                </strong>
                <span>
                    {isNachsorge
                        ? t('The stored aftercare week or question is missing or invalid.')
                        : t('The stored therapy material JSON is missing or invalid.')}
                </span>
            </div>
        );
    }

    const document = parsed.document;
    const availableAudiences = getAvailableAudiences(document);
    const blocks = resolveTherapyMaterialBlocks({
        document,
        audience,
        questionnaire,
        questionnaireResponse,
        labels: { yes: t('Yes'), no: t('No') },
    });
    const isResponse = Boolean(questionnaireResponse);

    const renderAnswer = (answerText: string, key: string): JSX.Element => (
        <div className={`therapy-material__answer ${answerText ? '' : 'therapy-material__answer--empty'}`} key={key}>
            {answerText || (isResponse ? t('No answer provided') : t('The patient answer will appear here.'))}
        </div>
    );

    const renderContentItem = ({ item, blockIndex, answers }: ResolvedTherapyMaterialBlock): JSX.Element => {
        const key = `${item.type}-${blockIndex}`;

        switch (item.type) {
            case 'header':
                return (
                    <h3 className="therapy-material__heading" key={key}>
                        {item.text}
                    </h3>
                );
            case 'paragraph':
                return (
                    <p className="therapy-material__paragraph" key={key}>
                        {item.text}
                    </p>
                );
            case 'display':
                return (
                    <p className="therapy-material__display" key={key}>
                        {item.text}
                    </p>
                );
            case 'image':
                return <TherapyMaterialImage item={item} key={key} />;
            case 'check':
                return (
                    <div className="therapy-material__check" key={key}>
                        <span className="therapy-material__check-icon" aria-hidden="true">
                            ✓
                        </span>
                        <span>{item.text}</span>
                    </div>
                );
            case 'listitem':
                return (
                    <div className="therapy-material__list-item" key={key}>
                        <span aria-hidden="true">•</span>
                        <span>{item.text}</span>
                    </div>
                );
            case 'chat':
                return (
                    <div className="therapy-material__chat" key={key}>
                        {item.character && <strong>{item.character}</strong>}
                        <p>{item.text}</p>
                    </div>
                );
            case 'textfield':
                return (
                    <section className="therapy-material__field" key={key}>
                        <h4>{item.text}</h4>
                        {renderAnswer(answers[0] ?? '', `${key}-answer`)}
                    </section>
                );
            case 'imageform':
                return (
                    <section className="therapy-material__interactive-image" key={key}>
                        <TherapyMaterialImage item={item} />
                        <h4>{t('Entries on this image')}</h4>
                        <ol>
                            {answers.map((answerText, rowIndex) => (
                                <li key={`${key}-${rowIndex}`}>
                                    {renderAnswer(answerText, `${key}-${rowIndex}-answer`)}
                                </li>
                            ))}
                        </ol>
                    </section>
                );
            case 'thoughtclouds':
                return (
                    <section className="therapy-material__thought-clouds" key={key}>
                        {item.path && <TherapyMaterialImage item={item} variant="small" />}
                        <div className="therapy-material__thought-cloud-grid">
                            {(item.prompts ?? []).map((prompt, promptIndex) => (
                                <div className="therapy-material__thought-cloud" key={`${key}-${prompt}`}>
                                    <strong>{prompt}</strong>
                                    {renderAnswer(answers[promptIndex] ?? '', `${key}-${promptIndex}-answer`)}
                                </div>
                            ))}
                        </div>
                    </section>
                );
            default:
                return (
                    <div className="therapy-material__unsupported" key={key}>
                        {t('Unsupported therapy material block')}: {item.type}
                    </div>
                );
        }
    };

    return (
        <article className="therapy-material">
            <header className="therapy-material__header">
                <div>
                    <span className="therapy-material__eyebrow">
                        {isNachsorge ? t('Aftercare') : t('Therapy Material')}
                    </span>
                    <h2>{document.title}</h2>
                    {isNachsorge && document.week !== undefined ? (
                        <span className="therapy-material__week">
                            {t('Aftercare week {{week}}', { week: document.week })}
                        </span>
                    ) : (
                        <span className="therapy-material__id">{document.id}</span>
                    )}
                </div>
                {availableAudiences.length > 1 && (
                    <div className="therapy-material__audience" role="group" aria-label={t('Content version')}>
                        {availableAudiences.map((availableAudience) => (
                            <button
                                className={audience === availableAudience ? 'active' : ''}
                                type="button"
                                aria-pressed={audience === availableAudience}
                                onClick={() => setAudience(availableAudience)}
                                key={availableAudience}
                            >
                                {availableAudience === 'female' ? t('Female version') : t('Male version')}
                            </button>
                        ))}
                    </div>
                )}
            </header>
            <div className="therapy-material__content">{blocks.map(renderContentItem)}</div>
        </article>
    );
};

export default TherapyMaterialRenderer;
